import {
  type Dispatch,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import { usePublishedHeight } from '../ui/use-published-height.ts';

/** What a step's own write came to: move on, or stay where the researcher is. */
export type ContinueOutcome = 'advance' | 'stay';

/**
 * What the footer's primary button does on a step that has something to settle before moving on —
 * a confirm or an approval — registered by the step through `useContinueAction`.
 *
 * A step with nothing to settle (already approved or confirmed, frozen, read-only, or a step with
 * no write) registers nothing, and the button is a plain move to the next step once the study has
 * reached it. A step whose document does not exist yet registers a disabled action whose hint says
 * what the document waits on, so the button is held with the step's reason rather than the shell's.
 */
export interface ContinueAction {
  /**
   * The step's own write. Resolves `'advance'` once the server accepted it, and `'stay'` on a
   * refusal or a confirm left open, having shown the refusal on the step itself. Never rejects.
   *
   * The step never navigates: on `'advance'` the shell reads the study again and moves on only once
   * that read shows the next step reached.
   */
  run: () => Promise<ContinueOutcome>;
  /** True when the step cannot run its write now: an open edit, an unsaved draft, a pending check. */
  disabled: boolean;
  /** Why the button is disabled, in the step's words; null when it is not. */
  blockedHint: string | null;
  /** The button's label; the footer's own when omitted. */
  label?: string;
  /** The button's label while `run` is in flight, e.g. 'Approving…'. */
  busyLabel?: string;
}

/**
 * The shell's slot for the step's Continue action: a state setter, so a step taking its action back
 * can clear the slot only while it still holds that action.
 */
export const StepFooterContext = createContext<Dispatch<SetStateAction<ContinueAction | null>>>(
  () => undefined,
);

/**
 * Registers what the footer's primary button does on the step that calls it, for as long as it is
 * mounted. Null is "nothing to settle", and registers nothing.
 *
 * One registration per step: the shell holds one action, and the latest registration wins. A
 * registrant taking its action back clears the slot only while it still holds that action, so one
 * unmounting never clears another's.
 *
 * `run` is called through a ref, so a step handing a new closure on every render does not re-render
 * the shell; the shell hears only when what the button shows changes.
 */
export const useContinueAction = (action: ContinueAction | null): void => {
  const register = useContext(StepFooterContext);
  const run = useRef(action?.run);
  // Before any effect, so a click after this commit runs this render's closure.
  useLayoutEffect(() => {
    run.current = action?.run;
  });

  const present = action !== null;
  const disabled = action?.disabled ?? false;
  const blockedHint = action?.blockedHint ?? null;
  const label = action?.label;
  const busyLabel = action?.busyLabel;

  useEffect(() => {
    if (!present) {
      return;
    }

    const registered: ContinueAction = {
      // A step that unmounted mid-click leaves nothing to run; staying is the honest answer.
      run: () => run.current?.() ?? Promise.resolve('stay'),
      disabled,
      blockedHint,
      ...(label === undefined ? {} : { label }),
      ...(busyLabel === undefined ? {} : { busyLabel }),
    };
    register(registered);

    return () => register((held) => (held === registered ? null : held));
  }, [register, present, disabled, blockedHint, label, busyLabel]);
};

/** The shell's sentence beside a primary button the study has not reached the step for. */
export const FINISH_THIS_STEP_HINT = 'Finish this step to carry on.';

export interface StepFooterPrimary {
  label: string;
  disabled: boolean;
  busy: boolean;
  /** Said beside the button, or nothing. */
  hint: string | null;
  onClick: () => void;
}

/**
 * The bar pinned to the bottom of every study step: Back on the left, and on the right the button
 * that moves on with whatever is keeping it from doing so. The last step has no primary.
 *
 * It grows when its hint wraps, so its drawn height is published as `--step-footer-height` for the
 * page's end and the drawer above it.
 */
export const StepFooter = ({
  onBack,
  primary,
}: {
  onBack: () => void;
  primary: StepFooterPrimary | null;
}) => {
  const bar = useRef<HTMLElement>(null);
  usePublishedHeight(bar, '--step-footer-height');

  return (
    <nav ref={bar} className="step-footer" aria-label="Study navigation" data-test-id="step-footer">
      <div className="step-footer-inner">
        <button type="button" className="text-link muted" data-test-id="step-back" onClick={onBack}>
          Back
        </button>
        {primary !== null && (
          <div className="step-continue">
            {primary.hint !== null && (
              <span className="artifact-meta" data-test-id="step-continue-blocked">
                {primary.hint}
              </span>
            )}
            <button
              type="button"
              className="button-primary"
              data-test-id="step-continue"
              disabled={primary.disabled || primary.busy}
              aria-busy={primary.busy || undefined}
              onClick={primary.onClick}
            >
              {primary.label}
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};
